import React, { useState, useEffect } from "react";
import { Plus, Trash2, X, Users, Loader2, CheckCircle, AlertCircle, Eye, Edit2 } from "lucide-react";
import { logPersonaChange } from "../utils/dataLineage";

const PersonaManagement = () => {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    personaName: '',
    role: '',
    age: '',
    experience: '',
    goals: '',
    motivations: '',
    painPoints: '',
    preferredTasks: '',
    scenario: ''
  });

  useEffect(() => {
    fetchPersonas();
  }, []);

  const fetchPersonas = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/persona_management/get_persona');
      if (!response.ok) throw new Error('Failed to fetch personas');
      
      const data = await response.json();
      const parsedPersonas = JSON.parse(data.persona);
      setPersonas(parsedPersonas);
    } catch (err) {
      console.error('Error fetching personas:', err);
      setError("Failed to load personas. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddPersona = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        persona_name: formData.personaName,
        role: formData.role,
        age: formData.age,
        experience: formData.experience,
        goals: formData.goals,
        motivations: formData.motivations,
        pain_points: formData.painPoints,
        preferred_tasks: formData.preferredTasks,
        scenario: formData.scenario
      });

      const response = await fetch(`/api/persona_management/add_persona?${params}`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to add persona');

      // Log persona creation
      await logPersonaChange(
        formData.personaName,
        formData.personaName,
        'CREATE',
        'persona_created',
        null,
        `Role: ${formData.role}, Age: ${formData.age}`
      );

      setSuccessMessage('Persona added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowAddModal(false);
      resetForm();
      fetchPersonas();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (persona) => {
    setSelectedPersona(persona);
    setFormData({
      personaName: persona['Persona Name'],
      role: persona['Role'],
      age: persona['Age'],
      experience: persona['Experience'],
      goals: persona['Goals'],
      motivations: persona['Motivations'],
      painPoints: persona['Pain Points'],
      preferredTasks: persona['Preferred Tasks'],
      scenario: persona['Scenario']
    });
    setShowEditModal(true);
  };

  const handleEditPersona = async (e) => {
    e.preventDefault();
    if (!selectedPersona) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/persona_management/update_persona', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          old_persona_name: selectedPersona['Persona Name'],
          persona_name: formData.personaName,
          role: formData.role,
          age: parseInt(formData.age),
          experience: formData.experience,
          goals: formData.goals,
          motivations: formData.motivations,
          pain_points: formData.painPoints,
          preferred_tasks: formData.preferredTasks,
          scenario: formData.scenario
        })
      });

      if (!response.ok) throw new Error('Failed to update persona');

      // Log changes to data lineage
      const personaId = formData.personaName;
      if (selectedPersona['Persona Name'] !== formData.personaName) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'persona_name', selectedPersona['Persona Name'], formData.personaName);
      }
      if (selectedPersona['Role'] !== formData.role) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'role', selectedPersona['Role'], formData.role);
      }
      if (selectedPersona['Age'] !== formData.age) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'age', selectedPersona['Age'], formData.age);
      }
      if (selectedPersona['Experience'] !== formData.experience) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'experience', selectedPersona['Experience'], formData.experience);
      }
      if (selectedPersona['Goals'] !== formData.goals) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'goals', selectedPersona['Goals'], formData.goals);
      }
      if (selectedPersona['Motivations'] !== formData.motivations) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'motivations', selectedPersona['Motivations'], formData.motivations);
      }
      if (selectedPersona['Pain Points'] !== formData.painPoints) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'pain_points', selectedPersona['Pain Points'], formData.painPoints);
      }
      if (selectedPersona['Preferred Tasks'] !== formData.preferredTasks) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'preferred_tasks', selectedPersona['Preferred Tasks'], formData.preferredTasks);
      }
      if (selectedPersona['Scenario'] !== formData.scenario) {
        await logPersonaChange(personaId, formData.personaName, 'UPDATE', 'scenario', selectedPersona['Scenario'], formData.scenario);
      }

      setSuccessMessage('Persona updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowEditModal(false);
      resetForm();
      fetchPersonas();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePersona = async () => {
    if (!selectedPersona) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/persona_management/delete-persona?persona=${encodeURIComponent(selectedPersona['Persona Name'])}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete persona');

      // Log persona deletion
      await logPersonaChange(
        selectedPersona['Persona Name'],
        selectedPersona['Persona Name'],
        'DELETE',
        'persona_deleted',
        `Role: ${selectedPersona['Role']}`,
        null
      );

      setSuccessMessage('Persona deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowDeleteModal(false);
      setSelectedPersona(null);
      fetchPersonas();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      personaName: '',
      role: '',
      age: '',
      experience: '',
      goals: '',
      motivations: '',
      painPoints: '',
      preferredTasks: '',
      scenario: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
    setError(null);
  };

  const openDeleteModal = (persona) => {
    setSelectedPersona(persona);
    setShowDeleteModal(true);
    setError(null);
  };

  const openViewModal = (persona) => {
    setSelectedPersona(persona);
    setShowViewModal(true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-teal)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}

      {/* Success Message */}
      {successMessage && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-green)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle style={{ width: '20px', height: '20px', color: 'var(--cares-green)' }} />
          <span style={{ color: 'var(--cares-green)', fontSize: '14px', fontWeight: '500' }}>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--cares-orange)' }} />
          <span style={{ color: 'var(--cares-orange)', fontSize: '14px', fontWeight: '500' }}>{error}</span>
        </div>
      )}

      {/* Action Bar */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Total Personas: <span style={{ fontWeight: '600', color: 'var(--cares-text-1)' }}>{personas.length}</span>
        </div>
        <button
          onClick={openAddModal}
          style={{
            background: 'linear-gradient(135deg, var(--cares-teal) 0%, var(--cares-teal) 100%)',
            color: 'var(--cares-text-max)',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(6, 182, 212, 0.3)',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <Plus style={{ width: '18px', height: '18px' }} />
          Add Persona
        </button>
      </div>

      {/* Personas Table */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--cares-surface-board)', borderBottom: '2px solid var(--cares-line-card)' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Persona Name</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Age</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Experience</th>
              <th style={{ padding: '16px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {personas.map((persona, index) => (
              <tr key={index} style={{ borderBottom: '1px solid var(--cares-line-card)', transition: 'background 0.2s' }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
              >
                <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)' }}>{persona['Persona Name']}</td>
                <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-label)' }}>{persona['Role']}</td>
                <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-label)' }}>{persona['Age']}</td>
                <td style={{ padding: '16px', fontSize: '14px', color: 'var(--cares-text-label)' }}>{persona['Experience']}</td>
                <td style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button
                      onClick={() => openViewModal(persona)}
                      style={{
                        background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                        color: 'var(--cares-blue)',
                        border: '1px solid var(--cares-blue)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-blue)'; e.currentTarget.style.color = 'var(--cares-text-max)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))'; e.currentTarget.style.color = 'var(--cares-blue)'; }}
                    >
                      <Eye style={{ width: '14px', height: '14px' }} />
                      View
                    </button>
                    <button
                      onClick={() => openEditModal(persona)}
                      style={{
                        background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
                        color: 'var(--cares-green)',
                        border: '1px solid var(--cares-green)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-green)'; e.currentTarget.style.color = 'var(--cares-text-max)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))'; e.currentTarget.style.color = 'var(--cares-green)'; }}
                    >
                      <Edit2 style={{ width: '14px', height: '14px' }} />
                      Edit
                    </button>
                    <button
                      onClick={() => openDeleteModal(persona)}
                      style={{
                        background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
                        color: 'var(--cares-orange)',
                        border: '1px solid var(--cares-orange)',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cares-orange)'; e.currentTarget.style.color = 'var(--cares-text-max)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))'; e.currentTarget.style.color = 'var(--cares-orange)'; }}
                    >
                      <Trash2 style={{ width: '14px', height: '14px' }} />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Persona Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>Add New Persona</h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--cares-text-label)'
                }}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            <form onSubmit={handleAddPersona} style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Persona Name *
                  </label>
                  <input
                    type="text"
                    name="personaName"
                    value={formData.personaName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Role *
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                      Age *
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--cares-line-strong)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                      Experience *
                    </label>
                    <input
                      type="text"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--cares-line-strong)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Goals *
                  </label>
                  <textarea
                    name="goals"
                    value={formData.goals}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Motivations *
                  </label>
                  <textarea
                    name="motivations"
                    value={formData.motivations}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Pain Points *
                  </label>
                  <textarea
                    name="painPoints"
                    value={formData.painPoints}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Preferred Tasks *
                  </label>
                  <textarea
                    name="preferredTasks"
                    value={formData.preferredTasks}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Scenario *
                  </label>
                  <textarea
                    name="scenario"
                    value={formData.scenario}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'var(--cares-surface-card)',
                    color: 'var(--cares-text-label)',
                    border: '1px solid var(--cares-line-strong)',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-teal) 0%, var(--cares-teal) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : null}
                  {isSubmitting ? 'Adding...' : 'Add Persona'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Persona Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>Edit Persona</h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--cares-text-label)'
                }}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <form onSubmit={handleEditPersona} style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Persona Name *
                  </label>
                  <input
                    type="text"
                    name="personaName"
                    value={formData.personaName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Role *
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Age *
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Experience *
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Goals *
                  </label>
                  <textarea
                    name="goals"
                    value={formData.goals}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Motivations *
                  </label>
                  <textarea
                    name="motivations"
                    value={formData.motivations}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Pain Points *
                  </label>
                  <textarea
                    name="painPoints"
                    value={formData.painPoints}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Preferred Tasks *
                  </label>
                  <textarea
                    name="preferredTasks"
                    value={formData.preferredTasks}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-3)', marginBottom: '6px' }}>
                    Scenario *
                  </label>
                  <textarea
                    name="scenario"
                    value={formData.scenario}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--cares-line-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    background: 'var(--cares-surface-card)',
                    color: 'var(--cares-text-label)',
                    border: '1px solid var(--cares-line-strong)',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : null}
                  {isSubmitting ? 'Updating...' : 'Update Persona'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Persona Modal */}
      {showViewModal && selectedPersona && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, color-mix(in srgb, var(--cares-teal) 14%, var(--cares-surface-card)) 0%, color-mix(in srgb, var(--cares-teal) 14%, var(--cares-surface-card)) 100%)'
            }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>
                  {selectedPersona['Persona Name']}
                </h2>
                <p style={{ fontSize: '14px', color: 'var(--cares-text-label)', margin: '4px 0 0 0' }}>
                  {selectedPersona['Role']}
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--cares-text-label)'
                }}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      Age
                    </label>
                    <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0 }}>{selectedPersona['Age']}</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      Experience
                    </label>
                    <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0 }}>{selectedPersona['Experience']}</p>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Goals
                  </label>
                  <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0, lineHeight: '1.6' }}>{selectedPersona['Goals']}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Motivations
                  </label>
                  <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0, lineHeight: '1.6' }}>{selectedPersona['Motivations']}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Pain Points
                  </label>
                  <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0, lineHeight: '1.6' }}>{selectedPersona['Pain Points']}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Preferred Tasks
                  </label>
                  <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0, lineHeight: '1.6' }}>{selectedPersona['Preferred tasks']}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    Scenario
                  </label>
                  <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', margin: 0, lineHeight: '1.6' }}>{selectedPersona['Scenario']}</p>
                </div>
              </div>
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: 'linear-gradient(135deg, var(--cares-teal) 0%, var(--cares-teal) 100%)',
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPersona && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--cares-line-card)'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--cares-text-1)', margin: 0 }}>Delete Persona</h2>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ fontSize: '14px', color: 'var(--cares-text-label)', margin: 0, lineHeight: '1.6' }}>
                Are you sure you want to delete the persona <strong style={{ color: 'var(--cares-text-1)' }}>"{selectedPersona['Persona Name']}"</strong>? This action cannot be undone.
              </p>
            </div>
            <div style={{
              padding: '16px 24px',
              background: 'var(--cares-surface-board)',
              borderTop: '1px solid var(--cares-line-card)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isSubmitting}
                style={{
                  background: 'var(--cares-surface-card)',
                  color: 'var(--cares-text-label)',
                  border: '1px solid var(--cares-line-strong)',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePersona}
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? 'var(--cares-surface-raised)' : 'var(--cares-orange)',
                  color: 'var(--cares-text-max)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isSubmitting ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : null}
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonaManagement;

